import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StatusSummaryCards = () => {
  const statusData = [
    {
      id: 'active',
      title: 'Active Assets',
      count: 387,
      percentage: 89.3,
      change: '+2.4%',
      changeType: 'increase',
      icon: 'CheckCircle',
      color: 'success',
      description: 'Currently deployed and operational',
      trend: [85, 87, 89, 91, 89, 87, 89],
      details: {
        laptops: 198,
        desktops: 124,
        servers: 32,
        printers: 18,
        monitors: 15
      }
    },
    {
      id: 'spare',
      title: 'Spare Inventory',
      count: 28,
      percentage: 6.5,
      change: '-1.2%',
      changeType: 'decrease',
      icon: 'Package',
      color: 'accent',
      description: 'Available for deployment',
      trend: [32, 30, 28, 26, 28, 29, 28],
      details: {
        laptops: 12,
        desktops: 8,
        servers: 3,
        printers: 3,
        monitors: 2
      }
    },
    {
      id: 'repair',
      title: 'Under Repair',
      count: 12,
      percentage: 2.8,
      change: '+0.8%',
      changeType: 'increase',
      icon: 'Wrench',
      color: 'warning',
      description: 'Currently being serviced',
      trend: [8, 10, 12, 14, 11, 10, 12],
      details: {
        laptops: 6,
        desktops: 3,
        servers: 1,
        printers: 1,
        monitors: 1
      }
    },
    {
      id: 'retired',
      title: 'Retired Assets',
      count: 6,
      percentage: 1.4,
      change: '0%',
      changeType: 'neutral',
      icon: 'Archive',
      color: 'secondary',
      description: 'End of lifecycle, awaiting disposal',
      trend: [6, 6, 6, 6, 6, 6, 6],
      details: {
        laptops: 2,
        desktops: 2,
        servers: 1,
        printers: 1,
        monitors: 0
      }
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'success':
        return {
          bg: 'bg-success/10',
          text: 'text-success',
          border: 'border-success/20',
          icon: 'text-success'
        };
      case 'warning':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          border: 'border-warning/20',
          icon: 'text-warning'
        };
      case 'accent':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          border: 'border-accent/20',
          icon: 'text-accent'
        };
      case 'secondary':
        return {
          bg: 'bg-secondary/10',
          text: 'text-secondary',
          border: 'border-secondary/20',
          icon: 'text-secondary'
        };
      default:
        return {
          bg: 'bg-primary/10',
          text: 'text-primary',
          border: 'border-primary/20',
          icon: 'text-primary'
        };
    }
  };

  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'increase':
        return 'TrendingUp';
      case 'decrease':
        return 'TrendingDown';
      default:
        return 'Minus';
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'increase':
        return 'text-success';
      case 'decrease':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const renderMiniChart = (trend, color) => {
    const max = Math.max(...trend);
    const min = Math.min(...trend);
    const range = max - min || 1;
    
    return (
      <div className="flex items-end space-x-1 h-8">
        {trend?.map((value, index) => {
          const height = ((value - min) / range) * 24 + 4;
          return (
            <div
              key={index}
              className={`w-1 rounded-sm ${getColorClasses(color)?.bg}`}
              style={{ height: `${height}px` }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {statusData?.map((status) => {
        const colors = getColorClasses(status?.color);
        
        return (
          <div
            key={status?.id}
            className="bg-card border border-border rounded-lg p-6 shadow-subtle hover:shadow-moderate transition-smooth group"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${colors?.bg} ${colors?.border}`}>
                <Icon name={status?.icon} size={24} className={colors?.icon} />
              </div>
              
              <div className="flex items-center space-x-1">
                <Icon 
                  name={getChangeIcon(status?.changeType)} 
                  size={16} 
                  className={getChangeColor(status?.changeType)} 
                />
                <span className={`text-sm font-medium ${getChangeColor(status?.changeType)}`}>
                  {status?.change}
                </span>
              </div>
            </div>
            {/* Main Content */}
            <div className="mb-4">
              <div className="flex items-baseline space-x-2 mb-1">
                <h3 className="text-3xl font-bold text-card-foreground">
                  {status?.count}
                </h3>
                <span className="text-sm text-muted-foreground">
                  ({status?.percentage}%)
                </span>
              </div>
              <p className="text-sm font-medium text-card-foreground mb-1">
                {status?.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {status?.description}
              </p>
            </div>
            {/* Mini Chart */}
            <div className="mb-4">
              {renderMiniChart(status?.trend, status?.color)}
            </div>
            {/* Asset Breakdown */}
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-card-foreground">Breakdown:</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {Object.entries(status?.details)?.map(([type, count]) => (
                  <div key={type} className="flex justify-between">
                    <span className="text-muted-foreground capitalize">{type}:</span>
                    <span className="font-medium text-card-foreground">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Action Button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full opacity-0 group-hover:opacity-100 transition-smooth"
              iconName="ExternalLink"
              iconPosition="right"
            >
              View Details
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default StatusSummaryCards;